/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 17th 2026, 10:48:01 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import React from "react";

import { ObjectKey, PrimaryKeyValue } from "lib/types";
import { addOrUpdateItem } from "lib/utils";
import { APIMany2ManyFormInput, OSActionButton, useToggleState } from "ui-widgets";
import { useControlledInput } from "ui-widgets/forms";
import { ConcreteAPIMany2ManyInputProps, GenericAPIMany2ManyFormInputProps } from "ui-widgets/forms/types";

type GenericAPIMany2ManyDropDownFormInputProps<PrimaryObject, RelatedObject> = Omit<
  GenericAPIMany2ManyFormInputProps<PrimaryObject, RelatedObject>,
  "children" | "getOptionLabel"
> &
  TextFieldProps & {
    onLoadInitial?: (initialValue: RelatedObject) => void;
    isFetchingAPIItem: boolean;
    getOptionLabel?: (option: RelatedObject) => string;
    onDeleteItem?: (relatedObjectId: PrimaryKeyValue<RelatedObject>) => void;
    renderUpdateItemPopUp?: (args: {
      item: RelatedObject;
      onUpdateItem: (relatedObject: RelatedObject) => void;
      closePopup: () => void;
      popUpOpen: boolean;
    }) => React.ReactNode;
  };

export type ConcreteAPIMany2ManyDropDownFormInputProps<PrimaryObject, RelatedObject> = ConcreteAPIMany2ManyInputProps<
  PrimaryObject,
  RelatedObject
>;

export const APIMany2ManyDropDownFormInput = <PrimaryObject, RelatedObject>(
  props: GenericAPIMany2ManyDropDownFormInputProps<PrimaryObject, RelatedObject>
) => {
  const {
    field: { onChange, value },
  } = useControlledInput<PrimaryObject>(props);

  const relatedObjectIds = (value ?? []) as PrimaryKeyValue<RelatedObject>[];

  const handleDelete = React.useCallback(
    (id: PrimaryKeyValue<RelatedObject>) => {
      const newIds = relatedObjectIds.filter((objId) => id !== objId);
      onChange(newIds);
      props?.onDeleteItem?.(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.schema_field_key, onChange, JSON.stringify(relatedObjectIds)]
  );

  const handleSelectOption = React.useCallback(
    (id: PrimaryKeyValue<RelatedObject>) => {
      if (relatedObjectIds.includes(id)) return handleDelete(id);
      onChange([...relatedObjectIds, id]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.schema_field_key, onChange, JSON.stringify(relatedObjectIds), handleDelete]
  );

  const { renderUpdateItemPopUp, label, getOptionLabel, getOptionComponent, ...otherProps } = props;
  const { itemIdProperty } = props;

  const [allItems, setAllItems] = React.useState<RelatedObject[]>([]);
  const [itemToEdit, setItemToEdit] = React.useState<RelatedObject>(null);
  const { handleClose: closeUpdatePopup, handleOpen: openUpdatePopup, open: updateItemPopupOpen } = useToggleState();
  const onSelectEditItem = (item: RelatedObject | null) => {
    setItemToEdit(item);
    openUpdatePopup();
  };

  return (
    <APIMany2ManyFormInput<PrimaryObject, RelatedObject> {...otherProps}>
      {({ filteredItems: fetchedItems }) => {
        const listItems = allItems?.length > 0 ? allItems : fetchedItems;
        return (
          <Stack direction="row" spacing={2}>
            <Autocomplete
              disablePortal
              fullWidth
              options={listItems}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    ...props.InputProps,
                    startAdornment: (
                      <>
                        {props.InputProps?.startAdornment}
                        {relatedObjectIds.map((id) => {
                          const item = listItems.find((item) => item[itemIdProperty] === id);
                          return (
                            <Chip
                              className="os-tags-chip os-tags-chip-hover"
                              key={`${id}`}
                              tabIndex={-1}
                              label={item ? getOptionLabel?.(item) ?? id : ""}
                              onClick={() => onSelectEditItem(item)}
                              onDelete={() => handleDelete(id)}
                            />
                          );
                        })}
                      </>
                    ),
                  }}
                  label={props.label}
                />
              )}
              getOptionLabel={
                getOptionLabel
                  ? (option) => {
                      if (Array.isArray(option)) return ""; // If the passed options is the array of actual values, it means we are in the text input part, so show nothing and let the chips do the talking.
                      return getOptionLabel(option) ?? "";
                    }
                  : null
              }
              isOptionEqualToValue={(option, value) => {
                return value?.includes(option?.[itemIdProperty]);
              }}
              value={relatedObjectIds ?? []}
              renderOption={(optionProps, option: any) => (
                <li
                  {...optionProps}
                  key={option[itemIdProperty]}
                  onClick={() => handleSelectOption(option[itemIdProperty])}
                >
                  {getOptionComponent?.(option) ?? option[itemIdProperty]}
                </li>
              )}
            />
            <div className="os-many2one-add-new-container">
              <IconButton color="primary" onClick={() => onSelectEditItem(null)}>
                <OSActionButton actionStyle="add" buttonType="floating" onClick={() => onSelectEditItem(null)} />
              </IconButton>
              {renderUpdateItemPopUp?.({
                popUpOpen: updateItemPopupOpen,
                closePopup: closeUpdatePopup,
                item: itemToEdit,
                onUpdateItem: (relatedObject) => {
                  setAllItems((prev) =>
                    addOrUpdateItem(
                      prev.length > 0 ? prev : fetchedItems,
                      relatedObject,
                      itemIdProperty as ObjectKey<RelatedObject>
                    )
                  );
                  setItemToEdit(relatedObject);
                  if (!relatedObjectIds.includes(relatedObject?.[itemIdProperty])) {
                    handleSelectOption(relatedObject?.[itemIdProperty]);
                  }
                },
              })}
            </div>
          </Stack>
        );
      }}
    </APIMany2ManyFormInput>
  );
};
